/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

import { Base64 } from "js-base64";
import faviconUrl from "$$RES/img/gbc_logo.ico?url";

modulum("ThemeService", function (context, cls) {
  /**
   * Theme Service to customize the application
   * @namespace gbc.ThemeService
   * @gbcService
   */
  class ThemeService /** @lends gbc.ThemeService */ {
    constructor() {
      /**
       * internal event emitter
       * @private
       */
      this._eventListener = new cls.EventListener();

      /**
       * current theme parsed values
       * @type {*}
       * @private
       */
      this._currentVariables = [];

      /**
       * current theme in DOM resources
       * @type {*}
       * @private
       */
      this._currentResources = {};

      /**
       * current theme resources pathes
       * @type {*}
       * @private
       */
      this._currentPathes = {};

      /**
       * list of usable themes
       * @type {*}
       * @private
       */
      // jshint ignore:start
      this._availableThemes = window.__gbcThemesInfo || __gbcThemesInfo__;
      // jshint ignore:end

      /**
       * current theme name
       * @type {string}
       * @private
       */
      this._currentThemeName = "";

      this._hasChanged = false;

      /**
       * collection of test functions used in theme conditions
       */
      this._conditionMatchers = {
        isMobile() {
          return cls.WindowHelper.isMobile();
        },
        isPhone() {
          return cls.WindowHelper.isPhone();
        },
        isTablet() {
          return cls.WindowHelper.isTablet();
        },
        isDesktop() {
          return !cls.WindowHelper.isMobile();
        },
        isTouchDevice() {
          return cls.WindowHelper.isTouchDevice();
        },
        isUR() {
          return window.gbc.__wrapper.isPlatformTypeNative();
        },
        isBrowser() {
          return window.gbc.__wrapper.isPlatformTypeBrowser();
        },
        isChrome() {
          return window.browserInfo.isChrome;
        },
        isEdge() {
          return window.browserInfo.isEdge;
        },
        isFirefox() {
          return window.browserInfo.isFirefox;
        },
        isOpera() {
          return window.browserInfo.isOpera;
        },
        isSafari() {
          return window.browserInfo.isSafari;
        },
        isAndroid() {
          return cls.WindowHelper.isAndroid();
        },
        isIOS() {
          return cls.WindowHelper.isIOS();
        }
      };
    }

    /**
     * test theme conditions against matchers
     * @param {Array<string>} conditions list of theme conditions
     * @return {boolean} true if all conditions matches
     * @private
     */
    _conditionsMatches(conditions) {
      let i = 0;
      const len = (conditions && conditions.length) || 0;
      for (; i < len; i++) {
        try {
          if (!this._conditionMatchers[conditions[i]]()) {
            return false;
          }
        } catch (e) {
          return false;
        }
      }
      return true;
    }

    /**
     * filter list of themes against their conditions
     * @param {Array} list list of themes
     * @return {Array} filtered list of themes
     * @private
     */
    _filterThemeList(list) {
      const result = [];
      let i = 0;
      const len = (list && list.length) || 0;
      for (; i < len; i++) {
        if (this._conditionsMatches(list[i].conditions)) {
          result.push(list[i]);
        }
      }
      return result;
    }

    /**
     * return the gutter X value
     * @returns {number}
     */
    getGutterX() {
      return this.getValue("gbc-grid-inner-gutter-x") || this.getValue("gbc-grid-inner-gutter");
    }

    /**
     * return the gutter Y value
     * @returns {number}
     */
    getGutterY() {
      return this.getValue("gbc-grid-inner-gutter-y") || this.getValue("gbc-grid-inner-gutter");
    }

    /**
     * get a value from theme
     * @param {string} id the theme value id
     * @return {*} the value
     */
    getValue(id) {
      return context.ThemeService._currentVariables[id];
    }

    /**
     * set a value to the local theme - internal use only
     * @param {string} id the theme value id
     * @param {*} value the value
     * @ignore
     */
    setValue(id, value) {
      context.ThemeService._currentVariables[id] = value;
    }

    /**
     * get a resource path from theme
     * @param {string} filename the theme resource id
     * @return {*} the resource path
     */
    getResource(filename) {
      if (gbc.buildTool === "vite") {
        if (this._resolvedResources?.[filename]) {
          return this._resolvedResources[filename];
        }
        const theme = this.getCurrentTheme();
        return `/ua/w/web/assets/theme/${theme}/resources/${filename}`;
      }
      return context.ThemeService._currentPathes.themes[context.ThemeService._currentThemeName].indexOf(filename) >= 0
        ? "themes/" + context.ThemeService._currentThemeName + "/resources/" + filename
        : "resources/" + filename;
    }

    /**
     * Thanks to Edge/IE, for need to be sure the current theme is well loaded
     * @returns {boolean} true if current theme is loaded
     * @private
     */
    _isCurrentThemeLoaded() {
      return Boolean(context.ThemeService._currentPathes.themes[context.ThemeService._currentThemeName]);
    }

    /**
     * Get available themes
     * @return {Array} the themes
     */
    getAvailableThemes() {
      return this._availableThemes;
    }

    /**
     * get current theme name
     * @return {string} the current theme name
     */
    getCurrentTheme() {
      return this._currentThemeName;
    }
    /**
     * parse theme variables from injected json
     * @return {{variables:Object, pathes:Object}} theme information
     * @private
     */
    _loadValues() {
      const styles = window.getComputedStyle(document.body, ":after"),
        vars = styles.getPropertyValue("content"),
        result = JSON.parse(Base64.fromBase64(vars)),
        variablesKeys = Object.keys(result.variables);
      let i = 0;
      const len = variablesKeys.length;
      for (; i < len; i++) {
        const key = variablesKeys[i];
        if (/^b64\(.*\)$/.test(result.variables[key])) {
          result.variables[key] = JSON.parse(Base64.fromBase64(result.variables[key].replace(/^b64\((.*)\)$/, "$1")));
        }
      }
      return result;
    }

    /**
     * load theme
     * @param {string} name theme name
     * @param {function} [callback] callback when theme has been loaded
     * @param {boolean} [noSave] true to avoid stored settings save
     */
    async loadTheme(name, callback, noSave) {
      const currentTheme = context.ThemeService._currentThemeName;
      if (!noSave) {
        context.StoredSettingsService?.saveTheme(name);
      }

      // if the theme is the same as current, just execute the callback
      if (currentTheme === name) {
        if (typeof callback === "function") {
          callback();
        }
        return;
      }

      this._hasChanged = (!context.queryStringTheme && currentTheme) || (context.queryStringTheme && name !== context.queryStringTheme);

      context.ThemeService._currentThemeName = name;

      if (gbc.buildTool === "legacy") {
        await this.loadThemeLegacy(name, callback);
      } else if (gbc.buildTool === "vite") {
        try {
          await this.loadThemeVite(name, callback);
        } catch (err) {
          context.LogService.log(`Cannot load ${name}, fallback to ${currentTheme}`);
          await this.loadThemeVite(currentTheme, callback);
        }
      } else {
        context.LogService.error("Unkown build tool, unable to load the theme");
      }
    }

    /**
     * Load theme for the legacy build tool
     * @param {*} name
     * @param {*} callback
     */
    async loadThemeLegacy(name, callback) {
      const cacheTimestamp = gbc.lastCompDate ? `?ct=${gbc.lastCompDate}` : "";

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.type = "text/css";
      link.href = `./themes/${name}/main.css${cacheTimestamp}`;
      link.onload = function () {
        const themeInfo = context.ThemeService._loadValues();
        context.ThemeService._currentVariables = themeInfo.variables;
        context.ThemeService._currentPathes = themeInfo.pathes;
        if (this._isCurrentThemeLoaded()) {
          if (this._currentResources.link) {
            this._currentResources.link.remove();
          }
          this._currentResources.link = link;
          context.classes.WidgetFactory.registerThemeOverrides(themeInfo.widgetFactory);
          window.requestAnimationFrame(
            function () {
              // TODO: remove this second requestAnimationFrame when GBC-2251 is done (and fixes the font issue)
              window.requestAnimationFrame(
                function () {
                  this._onThemeLoaded();
                  if (typeof callback === "function") {
                    callback();
                  }
                }.bind(this)
              );
            }.bind(this)
          );
        } else {
          context.LogService.error("Theme '" + name + "' cannot be loaded!");
          link.remove();
          this.loadTheme(this._availableThemes[0].name, callback);
        }
      }.bind(this);
      link.onerror = function () {
        context.LogService.error("Theme '" + name + "' cannot be loaded!");
        link.remove();
        this.loadTheme(this._availableThemes[0].name, callback);
      }.bind(this);
      cls.DOMElementHelper.insertAfter(link, this._currentResources.link || document.head.getElementsByTagName("title")[0]);
    }

    async loadThemeVite(name, callback) {
      // Load theme css file dynamically
      const themeObject = await import(`@themes/theme-${name}-definition/theme.js`); // switch to this!

      //* Map theme services variables, resource and config with new theme info
      const { variables, resources, styles, themeConfig } = themeObject;
      const themeService = context.ThemeService;
      themeService._currentVariables = variables;
      themeService._resources = resources;

      // Pre-resolve all dynamic resource imports so getResource() can return URLs synchronously
      const resolvedResources = {};
      await Promise.all(
        Object.entries(resources).map(async ([key, importer]) => {
          try {
            const mod = await importer();
            resolvedResources[key] = mod.default || mod;
          } catch {
            context.LogService.log(`Theme resource "${key}" could not be loaded`);
          }
        })
      );
      themeService._resolvedResources = resolvedResources;

      context.classes.WidgetFactory.registerThemeOverrides(themeConfig.widgetFactory);

      let currentStyleSheet = document.getElementById("currentTheme");
      if (!currentStyleSheet) {
        currentStyleSheet = document.createElement("style");
        currentStyleSheet.setAttribute("id", "currentTheme");
        document.head.appendChild(currentStyleSheet);
      }
      currentStyleSheet.setAttribute("theme-name", name);
      currentStyleSheet.innerHTML = styles;

      this._onThemeLoaded();
      if (typeof callback === "function") {
        callback();
      }
    }

    /**
     * updates theme dependant elements in loaded gbc
     * @private
     */
    _onThemeLoaded() {
      this._eventListener.emit(context.constants.widgetEvents.themeChange);
      try {
        document.getElementById("favicon_element").href = faviconUrl;
      } catch (e) {
        document.getElementById("favicon_element").href = context.ThemeService.getResource("img/gbc_logo.ico");
      }
      const session = context.SessionService.getCurrent();
      if (session) {
        // Tell native part to update icon as well
        context.__wrapper.nativeCall(
          context.__wrapper.param(
            {
              name: "applicationIcon",
              args: {
                type: "theme",
                icon: context.ThemeService.getResource("img/gbc_logo.ico")
              }
            },
            session.getCurrentApplication()
          )
        );

        const apps = session.getApplications(),
          len = (apps && apps.length) || 0;
        let i = 0;
        for (; i < len; i++) {
          const app = apps[i],
            layout = app && app.layout,
            model = app && app.model;
          if (layout) {
            layout.reset();
          }
          if (model) {
            model.getNodesByTag("Window").forEach(function (item) {
              item.getWidget().getLayoutEngine().reset(true);
            });
          }
        }
      }

      //once theme has been changed, tell native wrapper
      context.__wrapper.nativeCall(
        context.__wrapper.param(
          {
            name: "themeChanged",
            args: {
              name: `${this.getCurrentTheme()}_style`
            }
          },
          session?.getCurrentApplication()
        )
      );

      // If we choose a webkit rendering, use variable as scrollbarSize
      if (
        this.getValue("html-webkit-scrollbars-global-enable") &&
        "WebkitAppearance" in document.documentElement.style &&
        !cls.WindowHelper.isAndroid() &&
        !cls.WindowHelper.isIOS()
      ) {
        window.scrollBarSize = Number.parseInt(this.getValue("html-webkit-scrollbars-size"), 10); //offset for webkit scrollbar
      }
    }

    /**
     * fired when theme changed
     * @param {Hook} hook the hook
     * @return {HandleRegistration} the handle registration
     */
    whenThemeChanged(hook) {
      return this._eventListener.when(context.constants.widgetEvents.themeChange, hook);
    }

    /**
     * Finds the name of the dark version of the theme provided if it exists
     * @param {string} name
     * @return {string} the name of the dark theme if it exists, or the name provided as param otherwise
     */
    getDarkSchemeName(name) {
      const darkSchemeName = name + "_dark";
      return this._availableThemes.find((theme) => theme.name === darkSchemeName) ? darkSchemeName : name;
    }

    /**
     * Finds the name of the light version of the theme provided if it exists
     * @param {string} name
     * @return {string} the name of the light theme if it exists, or the name provided as param otherwise
     */
    getLightSchemeName(name) {
      if (!this.isDarkScheme(name)) {
        return name;
      }
      const lightSchemeName = name.slice(0, -"_dark".length);
      return this._availableThemes.find((theme) => theme.name === lightSchemeName) ? lightSchemeName : name;
    }

    /**
     * @param {string} name
     * @returns {boolean}
     */
    isDarkScheme(name) {
      return Boolean(name?.endsWith("_dark"));
    }

    /**
     * Checks if theme provided has a corresponding light/dark mode
     * @param {string} name
     * @returns {boolean}
     */
    hasMultipleSchemes(name) {
      if (this.isDarkScheme(name)) {
        return this.getLightSchemeName(name) !== name;
      }
      return this.getDarkSchemeName(name) !== name;
    }

    /**
     * load initial theme
     * @param {?string} initialTheme initial theme to load
     * @param {function} callback callback when theme has been loaded
     */
    initTheme(initialTheme, callback) {
      this._availableThemes = this._filterThemeList(this._availableThemes);
      if (this._availableThemes.length) {
        // Priority 1: initialTheme parameter
        let themeToLoad = initialTheme;
        if (!initialTheme) {
          // Priority 2: stored theme
          const storedTheme = context.StoredSettingsService.loadTheme();
          if (storedTheme) {
            if (
              this._availableThemes.find(function (item) {
                return item.name === storedTheme;
              })
            ) {
              themeToLoad = storedTheme;
            }
          }
          if (!themeToLoad) {
            // Priority 3: First in list, dark flavor if browser is set to dark mode
            themeToLoad = this._availableThemes[0].name;
            if (window.matchMedia) {
              if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
                themeToLoad = this.getDarkSchemeName(themeToLoad);
              }
            }
          }
        }
        this.loadTheme(themeToLoad, callback, Boolean(initialTheme));
      } else {
        this._displayConsoleScreenOfDeath();
      }
    }

    /**
     * Show critical error if no theme is available
     * @private
     */
    _displayConsoleScreenOfDeath() {
      window.critical.display("Internal failure: No applicable theme found.");
    }

    /**
     * Get media string for initial handshake
     * @return {string} - media string "small"|"medium"|"large"
     */
    getMediaString() {
      const smallMax = this.getValue("responsive-small-width");
      const mediumMax = this.getValue("responsive-medium-width");
      if (window.matchMedia("(max-width: " + smallMax + ")").matches) {
        return "small";
      } else if (window.matchMedia("(max-width: " + mediumMax + ")").matches) {
        return "medium";
      } else {
        return "large";
      }
    }

    /**
     * Get the minimal row Height
     * @returns {number}
     */
    getTableMinimalRowHeight() {
      return cls.WindowHelper.isTouchDevice()
        ? this.getValue("gbc-table-minimal-row-height-on-touchscreen")
        : this.getValue("gbc-table-minimal-row-height");
    }

    /**
     * When UR wants to redefine font
     * @param {FontData} fontData - data related to font to set
     *
     */
    setFontData(fontData) {
      document.querySelector("body").style.fontSize = fontData?.size || null;
      document.querySelector("body").style.fontFamily = fontData?.family || null;
      document.querySelector("body").style.fontWeight = fontData?.weight?.toString() || null;
      document.querySelector("body").style.fontStyle = fontData?.italic ? "italic" : null;
    }

    /**
     * Get defined themed color corresponding to color name passed as argument
     * @param {string} color - color name
     * @param {boolean} isReverse - whether we want the corresponding background color
     * @returns {string} returns the color hexadecimal code
     */
    resolveThemedColor(color, isReverse = false) {
      if (!color) {
        return null;
      }
      // color may be of type number (BDL old feature)
      // 0=white, 1=yellow, 2=magenta, 3=red, 4=cyan, 5=green, 6=blue, 7=black
      if (Number.isInteger(color)) {
        color = gbc.constants.colorNumber[color];
        if (!color) {
          return null;
        }
      }
      // If color is set as 'RED', convert it to 'red' or it won't grab any json variable and take default css color
      if (color === color.toUpperCase()) {
        color = color.toLowerCase();
      }
      let autoColor = color;
      if (isReverse) {
        autoColor += "-reverse";
      }
      const themedColor = context.ThemeService.getValue("gbc-genero-" + autoColor);
      return themedColor || color;
    }
  }

  gbc.ThemeService = new ThemeService();
});
